// The Review cards' derived lines (0051 S1a): the "Why we're asking" line,
// the ONE References allowlist (provenanceLines; slice 3's drawer and slice
// 8's History call it too), the "Settles:" line, the amend's ruled lines and
// the saved answer's label, and the confirm option's routing id (0074), and the
// shared confirm card's act words (0074). Pure: no React. Every founder string
// built here is an exported constant or builder, so a test pins the words.
import type { components } from "../api/types";
import { stripAnchors } from "./stripAnchors";

type Node = components["schemas"]["Node"];

// The tree meta pieces these lines read, as the queues serve them (their served
// context's meta, 0051 S1c: a subset of the tree's meta, each piece as stored).
// `ring_status` is typed loosely, so it is unknown here and read defensively.
// The product's name and idea ride along for the cards' anchors to the root
// (rootNameOf, lib/displayName.ts).
export type ReviewTreeMeta = {
  title?: string | null;
  idea?: string | null;
  topics?: Record<string, Record<string, unknown>>;
  completeness_targets?: Record<string, Record<string, unknown>>;
  ring_status?: unknown;
};

export const WHY_PREFIX = "Why we're asking: ";
// Between the summary and where the node sits ("<area>, <layer>").
export const PLACE_SEPARATOR = " · ";
export const SETTLES_PREFIX = "Settles: ";
// 0051 S1b: a scope question's Why line, its out-of-scope line and 0170's list,
// in two forms (RULED 2026-10-06): a collision-born question names the
// obligation its listed decisions need; any other names the decisions that
// cross the line.
export const SCOPE_WHY_ONE = "a decision in your spec crosses a line in your out-of-scope list";
export const SCOPE_WHY_MANY = "decisions in your spec cross a line in your out-of-scope list";
export const SCOPE_WHY_NEEDS = "your spec needs ";
export const SCOPE_WHY_RULES_OUT = ", which a line in your out-of-scope list rules out";
export const OUT_OF_SCOPE_PREFIX = "Out of scope: ";
export const CROSSING_HEADING = "Decisions that cross this line";
export const NEEDS_HEADING = "Decisions that need it";
// 0051 S1c (RULED 2026-10-07): the Review page's line under the tabs when the
// active tab's list failed to load (never an empty page or a false "caught up").
export const LIST_LOAD_ERROR_LINE = "This list didn't load. Reload the page to try again.";

// 0074: the routing id of the engine's confirm option - the mirror of
// planner.ConfirmPickID (orchestrator/internal/planner/intent_source.go). Go
// attaches the option first on an exploratory playback with a non-blank read
// and no Go-authored fork; a card finds it by this id, never by its words.
// Trace-invariant T33
// (orchestrator/internal/contract/trace_invariants/t33_test.go) pins this
// literal against the Go constant and keeps it the app's only spelling.
export const CONFIRM_PICK = "CONFIRM";

// 0074: the shared confirm card's act words, and the saved state's word that
// both question cards read.
export const EDIT_ACT = "Edit";
export const SAVE_ACT = "Save";
export const SAVED_WORD = "Saved";
export const SAVING_WORD = "saving…";
export const SAVE_FAILED_LEAD = "Couldn't save: ";

// 0051 slice 3: the amend's ruled lines (0148 A29/A34/A36, FD-1 (c); RULED as
// shipped 2026-10-12), read by the covered-by row and the spec drawer, never
// spelled twice.
export const AMEND_SAVED_LINE = "Saved. Your spec doesn't change until you approve the replacement.";
export const AMEND_REPLACES_LEAD = "Amending replaces ";
export const AMEND_REPLACES_TAIL = " with a new decision for you to approve.";
export const SAVE_AMENDMENT_ACT = "Save amendment";

// 0148 A34/A36: the amend endpoint's refusals in founder language, mapped by
// CODE - never by the server's own message text, which stays for API callers
// and operators. Every other code (or none: a non-JSON body, a network
// failure) reads as the generic line. amend.same_unserved (0160, RULED
// 2026-10-15) is a second change to a decision whose change still waits; the
// spec drawer shows its line above AMEND_SAVED_LINE. amend.another_unserved
// (0160, RULED 2026-10-20, replacing the 2026-10-12 one-at-a-time line) is a
// change to a decision that shares a requirement with one whose change still
// waits: changes to decisions that share none wait together.
export function amendRefusalLine(code: string | undefined): string {
  switch (code) {
    case "amend.run_in_flight":
      return "We're working on your spec, so this decision can't be changed right now.";
    case "amend.another_unserved":
      return "You're already changing a decision that covers the same ground. This change can wait until that one lands.";
    case "amend.same_unserved":
      return "A change to this decision is already waiting.";
    default:
      return "This decision can't be changed from here right now.";
  }
}

// 0168 D: the label over a question's saved answer, read by the authority card and
// the spec drawer.
export const SAVED_ANSWER_LABEL = "Your saved answer";

export function fromAnswerLine(title: string): string {
  return `From your answer to "${title}"`;
}

export function replacesLine(title: string): string {
  return `Replaces "${title}"`;
}

export function buildsOnLine(title: string): string {
  return `Builds on "${title}"`;
}

function asString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
}

function fieldsOf(node: Node): Record<string, unknown> {
  return (node.fields ?? {}) as Record<string, unknown>;
}

// nodePlace names where a node sits: `area` is the display name of the FIRST
// id in fields.topics (an array of topic ids) that resolves in meta.topics to
// a named topic; `layer` is meta.ring_status[<ring key>].display_name, the
// ring key being String(fields.ring) for a number (the stored form, e.g. 6)
// or the string itself. A missing piece is "".
export function nodePlace(
  node: Node,
  meta: ReviewTreeMeta | undefined,
): { area: string; layer: string } {
  const fields = fieldsOf(node);
  let area = "";
  const topics = meta?.topics;
  if (topics !== undefined && Array.isArray(fields.topics)) {
    for (const id of fields.topics) {
      if (typeof id !== "string") continue;
      const name = asString(asRecord(topics[id])?.display_name).trim();
      if (name !== "") {
        area = name;
        break;
      }
    }
  }
  const ring = fields.ring;
  const ringKey = typeof ring === "number" ? String(ring) : asString(ring);
  const layer =
    ringKey === ""
      ? ""
      : asString(asRecord(asRecord(meta?.ring_status)?.[ringKey])?.display_name).trim();
  return { area, layer };
}

// placeSuffix is " · <area>, <layer>" (only the place pieces that exist), or ""
// when neither resolves - the place half every Why line ends with.
function placeSuffix(node: Node, meta: ReviewTreeMeta | undefined): string {
  const { area, layer } = nodePlace(node, meta);
  const place = area !== "" && layer !== "" ? `${area}, ${layer}` : area || layer;
  return place !== "" ? PLACE_SEPARATOR + place : "";
}

// whyWeAreAskingLine is "Why we're asking: <summary> · <area>, <layer>" (only
// the place pieces that exist), or "" when the node carries no summary.
export function whyWeAreAskingLine(node: Node, meta: ReviewTreeMeta | undefined): string {
  const summary = stripAnchors(asString(fieldsOf(node).summary)).trim();
  if (summary === "") return "";
  return WHY_PREFIX + summary + placeSuffix(node, meta);
}

// The collision label's text (0146 T6's fields.entailed_obligation_label), for
// the labelled Why form. QuestionCard reads it only where its T31-pinned
// collision read holds.
export function obligationLabelText(node: Node): string {
  return stripAnchors(asString(fieldsOf(node).entailed_obligation_label)).trim();
}

export type CrossingDecision = { id: string; title: string };

// crossingDecisions is 0170's list: the string entries of
// fields.reconciliation_cluster_ids, in served order, each id once, whose tree
// copy is a CONFIRMED decision with a non-blank title (anchor-stripped). A
// member missing from the map, of another type or status, or blank-titled is
// left out, never printed as a raw id.
export function crossingDecisions(node: Node, nodeById: ReadonlyMap<string, Node>): CrossingDecision[] {
  const raw = fieldsOf(node).reconciliation_cluster_ids;
  if (!Array.isArray(raw)) return [];
  const out: CrossingDecision[] = [];
  for (const id of raw) {
    if (typeof id !== "string" || out.some((c) => c.id === id)) continue;
    const member = nodeById.get(id);
    if (member === undefined || member.type !== "decision" || member.status !== "confirmed") continue;
    const title = stripAnchors(member.title ?? "").trim();
    if (title === "") continue;
    out.push({ id, title });
  }
  return out;
}

// scopeWhyLine is a scope question's Why line, in one of two forms (RULED
// 2026-10-06): with a non-blank obligation label, "Why we're asking: your spec
// needs <label>, which a line in your out-of-scope list rules out" (whatever
// the count); otherwise "Why we're asking: a decision in your spec crosses a
// line in your out-of-scope list" for one crossing decision and "decisions in
// your spec cross a line ..." for any other count. Either ends with the place
// suffix. It never reads fields.summary.
export function scopeWhyLine(
  node: Node,
  meta: ReviewTreeMeta | undefined,
  crossingCount: number,
  obligationLabel = "",
): string {
  const label = obligationLabel.trim();
  return (
    WHY_PREFIX +
    (label !== ""
      ? SCOPE_WHY_NEEDS + label + SCOPE_WHY_RULES_OUT
      : crossingCount === 1
        ? SCOPE_WHY_ONE
        : SCOPE_WHY_MANY) +
    placeSuffix(node, meta)
  );
}

// A character that continues an id; anything else (or either end of the
// title) bounds a whole token.
const ID_CHAR = /[A-Za-z0-9-]/;

function containsWholeToken(text: string, token: string): boolean {
  if (token === "") return false;
  for (let at = text.indexOf(token); at >= 0; at = text.indexOf(token, at + 1)) {
    const before = at === 0 ? "" : text.charAt(at - 1);
    const after = text.charAt(at + token.length);
    if ((before === "" || !ID_CHAR.test(before)) && (after === "" || !ID_CHAR.test(after))) {
      return true;
    }
  }
  return false;
}

// titleNamesCitedDecision is true when the question's (anchor-stripped) title
// contains, as a whole token (case-sensitive), the id of a decision it
// `cites`. That is the default amend carrier: its title is the fixed template
// "Founder amendment to decision <target id>" (a decision's later carriers,
// 0160: "Founder amendment <n> to decision <target id>") and it cites its
// target, so a line built from its title would print a raw id. Keyed on the
// title, not on an amend marker field: a carrier-arm question with a human
// title also carries that marker and keeps its line. It fails closed: a cited
// id whose target the map does not hold counts too (the line is then hidden,
// never a raw id shown); a cited target that IS in the map counts only as a
// decision, so a question citing the root idea never matches.
export function titleNamesCitedDecision(q: Node, nodeById: ReadonlyMap<string, Node>): boolean {
  const title = stripAnchors(q.title ?? "");
  for (const ref of q.refs ?? []) {
    if (ref.rel !== "cites") continue;
    const target = nodeById.get(ref.to);
    if (target !== undefined && target.type !== "decision") continue;
    if (containsWholeToken(title, ref.to)) return true;
  }
  return false;
}

export type ProvenanceLines = { fromAnswer: string[]; replaces: string[]; buildsOn: string[] };
// One allowlisted line with the id of the node it names.
export type ProvenanceSource = { id: string; line: string };
export type ProvenanceSources = {
  fromAnswer: ProvenanceSource[];
  replaces: ProvenanceSource[];
  buildsOn: ProvenanceSource[];
};

// provenanceLines is the ONE References allowlist, keyed by rel and carrier
// type and worded from the carrier's side:
//   - a decision derived-from / extracted-from a question: From your answer
//     to "<title>" (any question status), except a default amend carrier
//     (titleNamesCitedDecision), whose replacement's own supersedes ref
//     already says Replaces "<target title>";
//   - a decision that supersedes a CONFIRMED decision: Replaces "<title>";
//   - a question or decision that cites a CONFIRMED decision: Builds on
//     "<title>".
// Everything else is hidden: any other rel or carrier, a target missing from
// the map (never a raw id), the carrier itself, the root idea, an empty title,
// a rejected or proposed Replaces / Builds on target. Each list keeps ref
// order and lists a target once; a target in `replaces` is not repeated in
// `buildsOn`. A caller may pass ids to leave out of buildsOn (a scope card
// lists its crossing decisions under their own heading).
// The id-carrying form is provenanceSources (the spec drawer's buttons).
export function provenanceLines(
  node: Node,
  nodeById: ReadonlyMap<string, Node>,
  omit?: ReadonlySet<string>,
): ProvenanceLines {
  const s = provenanceSources(node, nodeById, omit);
  return {
    fromAnswer: s.fromAnswer.map((l) => l.line),
    replaces: s.replaces.map((l) => l.line),
    buildsOn: s.buildsOn.map((l) => l.line),
  };
}

// provenanceSources is provenanceLines' one rule, each line with the id of the
// node it names, so a surface can open that node (0051 slice 3: the spec
// drawer's lines are buttons when the node is a row). The words stay
// provenanceLines', spelled once by the builders above.
export function provenanceSources(
  node: Node,
  nodeById: ReadonlyMap<string, Node>,
  omit?: ReadonlySet<string>,
): ProvenanceSources {
  type Line = ProvenanceSource;
  const fromAnswer: Line[] = [];
  const replaces: Line[] = [];
  const buildsOn: Line[] = [];
  const add = (list: Line[], id: string, line: string) => {
    if (!list.some((l) => l.id === id)) list.push({ id, line });
  };
  const carrierType = node.type;
  for (const ref of node.refs ?? []) {
    const target = nodeById.get(ref.to);
    if (target === undefined || target.id === node.id || target.type === "idea") continue;
    const title = stripAnchors(target.title ?? "").trim();
    if (title === "") continue;
    if (
      carrierType === "decision" &&
      (ref.rel === "derived-from" || ref.rel === "extracted-from") &&
      target.type === "question"
    ) {
      if (!titleNamesCitedDecision(target, nodeById)) add(fromAnswer, target.id, fromAnswerLine(title));
    } else if (
      carrierType === "decision" &&
      ref.rel === "supersedes" &&
      target.type === "decision" &&
      target.status === "confirmed"
    ) {
      add(replaces, target.id, replacesLine(title));
    } else if (
      (carrierType === "question" || carrierType === "decision") &&
      ref.rel === "cites" &&
      target.type === "decision" &&
      target.status === "confirmed"
    ) {
      add(buildsOn, target.id, buildsOnLine(title));
    }
  }
  const replaced = new Set(replaces.map((l) => l.id));
  return {
    fromAnswer,
    replaces,
    buildsOn: buildsOn.filter((l) => !replaced.has(l.id) && !omit?.has(l.id)),
  };
}

// settlesLine is "Settles: <label>, <label>" for the ids in
// fields.satisfies_requirements that resolve through
// meta.completeness_targets[*].required_decisions[*] ({ id, label }):
// resolved labels only, deduped, in the ids' order. "" when none resolves.
export function settlesLine(node: Node, meta: ReviewTreeMeta | undefined): string {
  const raw = fieldsOf(node).satisfies_requirements;
  if (!Array.isArray(raw) || raw.length === 0) return "";
  const labelById = new Map<string, string>();
  for (const target of Object.values(meta?.completeness_targets ?? {})) {
    const required = asRecord(target)?.required_decisions;
    if (!Array.isArray(required)) continue;
    for (const entry of required) {
      const rd = asRecord(entry);
      const id = asString(rd?.id);
      const label = stripAnchors(asString(rd?.label)).trim();
      if (id !== "" && label !== "" && !labelById.has(id)) labelById.set(id, label);
    }
  }
  const labels: string[] = [];
  for (const id of raw) {
    if (typeof id !== "string") continue;
    const label = labelById.get(id);
    if (label !== undefined && !labels.includes(label)) labels.push(label);
  }
  return labels.length === 0 ? "" : SETTLES_PREFIX + labels.join(", ");
}
